import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { formatBRL, STATUS_STYLES, DOC_TYPES } from "@/lib/constants";
import { Download, CheckCircle2, Loader2, ShieldCheck, PenLine } from "lucide-react";

export default function PublicSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [sig, setSig] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);
  const [approved, setApproved] = useState(false);
  const [rejected, setRejected] = useState(false);

  const load = () => axios.get(`${API}/public/documents/${token}`).then(({ data }) => {
    setData(data); setName(data.client?.name || "");
    if (data.document?.status === "Assinado") setSigned(true);
    if (data.document?.status === "Aprovado") setApproved(true);
    if (data.document?.status === "Recusado") setRejected(true);
  }).catch(() => setErr(true));
  useEffect(() => { load(); }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">Documento não encontrado.</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const { document: doc, company, client } = data;
  const pdfUrl = `${API}/public/documents/${token}/pdf`;
  const isBudget = doc.type === "budget";

  const approve = async () => {
    setBusy(true);
    try { await axios.post(`${API}/public/documents/${token}/approve`); setApproved(true); toast.success("Orçamento aprovado!"); load(); }
    catch (e) { toast.error("Erro ao aprovar"); } finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try { await axios.post(`${API}/public/documents/${token}/reject`); setRejected(true); toast.success("Orçamento recusado."); load(); }
    catch (e) { toast.error("Erro"); } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!sig) { toast.error("Assine no campo"); return; }
    if (!name) { toast.error("Informe seu nome"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/documents/${token}/sign`, { name, signature: sig });
      setSigned(true); toast.success("Documento assinado com sucesso!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-muted/40 py-6 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          {company?.logo ? <img src={company.logo} alt="logo" className="h-12 w-12 rounded-xl object-cover border" /> : <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-display font-bold text-lg">{(company?.name || "D").slice(0, 1)}</div>}
          <div><p className="font-display font-extrabold text-lg">{company?.fantasy_name || company?.name}</p><p className="text-xs text-muted-foreground">{company?.phone || company?.whatsapp}</p></div>
        </div>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div><p className="text-xs font-semibold px-2 py-0.5 rounded-md bg-accent inline-block">{DOC_TYPES[doc.type]?.label}</p><h1 className="font-display font-extrabold text-xl mt-1">{doc.number}</h1></div>
            <Badge variant="outline" className={STATUS_STYLES[doc.status] || ""}>{doc.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Cliente: <span className="font-medium text-foreground">{client?.name}</span></p>
          {doc.description && <p className="text-sm mt-3">{doc.description}</p>}

          {doc.items?.length > 0 && (
            <div className="mt-4 space-y-1 border-t pt-3">
              {doc.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm"><span>{it.description} <span className="text-muted-foreground">x{it.qty}</span></span><span>{formatBRL(Number(it.qty) * Number(it.unit_price))}</span></div>
              ))}
            </div>
          )}
          <div className="flex justify-between font-display font-extrabold text-lg mt-3 pt-3 border-t"><span>Total</span><span className="text-secondary">{formatBRL(doc.total)}</span></div>
          {(doc.warranty?.term || doc.warranty?.covered) && (
            <div className="mt-3 rounded-lg bg-accent p-3 text-xs text-muted-foreground space-y-1">
              {doc.warranty?.term && <p><span className="font-semibold text-foreground">Garantia:</span> {doc.warranty.term}</p>}
              {doc.warranty?.covered && <p><span className="font-semibold text-foreground">Coberto:</span> {doc.warranty.covered}</p>}
              {doc.warranty?.not_covered && <p><span className="font-semibold text-foreground">Não coberto:</span> {doc.warranty.not_covered}</p>}
            </div>
          )}
          {doc.clauses?.length > 0 && (
            <div className="mt-4 border-t pt-3" data-testid="public-clauses">
              <p className="font-semibold text-sm mb-2">Cláusulas e condições</p>
              <div className="space-y-2">
                {doc.clauses.map((c, i) => (
                  <div key={i} className="text-xs"><p className="font-medium text-foreground">{c.title}</p><p className="text-muted-foreground">{c.text}</p></div>
                ))}
              </div>
            </div>
          )}

          {doc.photos?.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-4">
              {doc.photos.map((p, i) => <img key={i} src={p.data} alt="" className="w-full h-20 object-cover rounded-lg border" />)}
            </div>
          )}
        </Card>

        <a href={pdfUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="w-full h-11" data-testid="public-download-pdf"><Download className="h-4 w-4 mr-1" /> Ver / baixar PDF completo</Button></a>

        {isBudget && !approved && !rejected && !signed && (
          <Card className="p-5 space-y-3" data-testid="public-approval-card">
            <h2 className="font-display font-bold text-lg">Aprovação do orçamento</h2>
            <p className="text-sm text-muted-foreground">Revise os valores e condições acima. Você aprova este orçamento?</p>
            <div className="grid grid-cols-2 gap-2">
              <Button className="h-12 bg-secondary hover:bg-secondary/90" onClick={approve} disabled={busy} data-testid="public-approve-btn"><CheckCircle2 className="h-4 w-4 mr-1" /> Aprovar</Button>
              <Button variant="outline" className="h-12" onClick={reject} disabled={busy} data-testid="public-reject-btn">Recusar</Button>
            </div>
          </Card>
        )}
        {approved && !signed && <Card className="p-3 bg-secondary/10 border-secondary/30 text-sm text-secondary font-medium text-center">✓ Orçamento aprovado. Agora você pode assinar abaixo.</Card>}
        {rejected && <Card className="p-4 bg-red-500/10 border-red-500/30 text-center"><p className="font-semibold text-red-600">Orçamento recusado</p><p className="text-xs text-muted-foreground">O prestador foi notificado.</p></Card>}

        {signed ? (
          <Card className="p-6 text-center bg-secondary/10 border-secondary/30">
            <CheckCircle2 className="h-12 w-12 mx-auto text-secondary" />
            <p className="font-display font-bold text-lg mt-2 text-secondary">Documento assinado!</p>
            <p className="text-sm text-muted-foreground">Obrigado. O prestador foi notificado.</p>
            {doc.signature?.audit_id && <p className="text-xs text-muted-foreground mt-1 font-mono">ID: {doc.signature.audit_id}</p>}
          </Card>
        ) : rejected ? null : (isBudget && !approved) ? null : (
          <Card className="p-5 space-y-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><PenLine className="h-5 w-5" /> Assinar documento</h2>
            <p className="text-sm text-muted-foreground">Confira as informações acima e assine abaixo para confirmar.</p>
            <div><Label>Seu nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11" data-testid="public-sign-name" /></div>
            <SignaturePad onChange={setSig} />
            <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Data, hora e identificador serão registrados para segurança.</p>
            <Button className="w-full h-12" onClick={confirm} disabled={busy} data-testid="public-sign-confirm">{busy ? "Registrando..." : "Confirmar assinatura"}</Button>
          </Card>
        )}
        <p className="text-center text-xs text-muted-foreground py-4">Documento gerado por NotaHoje</p>
      </div>
    </div>
  );
}
